import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import type { OptionTimeframe } from "../components/DashBoard/GTT/gttTypes";
import { reduce } from "rxjs";
import { number } from "zod";

export interface ChargesSummaryVisible {
  memorizedLTP: number;
  buySell: "SELL" | "BUY";
  exchange: string;
  instrumentType: string;
  price: number; // will act as LTP as well
  quantity: number;
  symbol: string;
  tradeType: OptionTimeframe;
  isVisible: boolean;
}

export type ConfigKey =
  | "banknifty_max_lot"
  | "finnifty_max_lot"
  | "midcapnifty_max_lot"
  | "nifty50_max_lot"
  | "bankex_max_qty"
  | "banknifty_max_qty"
  | "finnifty_max_qty"
  | "midcapnifty_max_qty"
  | "nifty50_max_qty"
  | "nifty_next50_max_qty"
  | "sensex50_max_qty"
  | "sensex_max_qty";

export type RemoteConfigGetter = Record<ConfigKey, number>;

export type orderSummary = {
  stockExchnage: string;
  mktinstrumentType: string;
  marketSegmentId: number;
  tradeType: OptionTimeframe;
  transactionType: "SELL" | "BUY";
  price: number;
  lotSize: number
};

//  state type
export interface OrdersState {
  chargesSummaryVisible: Partial<ChargesSummaryVisible>;
  remoteConfigGetter: Partial<RemoteConfigGetter>;
  orderSummary: Partial<orderSummary>;
  margin: Partial<Margin>;
}

export interface Margin {
  requiredMargin: number,
  availableMargin: number,
}

// Initial state with all fields as undefined
const initialState: OrdersState = {
  chargesSummaryVisible: {
    memorizedLTP: undefined,
    buySell: undefined,
    exchange: undefined,
    instrumentType: undefined,
    price: undefined,
    quantity: undefined,
    symbol: undefined,
    tradeType: undefined,
    // isVisible: undefined,
    isVisible: false,
  },

  remoteConfigGetter: {
    banknifty_max_lot: undefined,
    finnifty_max_lot: undefined,
    midcapnifty_max_lot: undefined,
    nifty50_max_lot: undefined,
    // Quantity
    bankex_max_qty: undefined,
    banknifty_max_qty: undefined,
    finnifty_max_qty: undefined,
    midcapnifty_max_qty: undefined,
    nifty50_max_qty: undefined,
    nifty_next50_max_qty: undefined,
    sensex50_max_qty: undefined,
    sensex_max_qty: undefined,
  },
  orderSummary: {
    stockExchnage: undefined,
    mktinstrumentType: undefined,
    marketSegmentId: undefined,
    tradeType: undefined,
    transactionType: undefined,
    price: undefined,
  },
  margin: {
    requiredMargin: undefined,
    availableMargin: undefined,
  }
};

const OrdersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    // Set the entire state
    setChargesSummaryVisible(
      state,
      action: PayloadAction<ChargesSummaryVisible>
    ) {
      state.chargesSummaryVisible = action.payload;
    },

    // Reset only the chargesSummaryVisible part to initial state
    resetChargesSummaryVisible(state) {
      state.chargesSummaryVisible = initialState.chargesSummaryVisible;
    },

    setChargesSummaryVisibleKey(
      state: OrdersState,
      action: PayloadAction<{ key: keyof ChargesSummaryVisible; value: any }>
    ) {
      const { key, value } = action.payload;
      if (state.chargesSummaryVisible) {
        state.chargesSummaryVisible[key] = value;
      }
    },

    // Set Remote Config
    setRemoteConfigGetter(state, action: PayloadAction<RemoteConfigGetter>) {
      state.remoteConfigGetter = action.payload;
    },

    // Set Order Summary
    setOrderSummary(state, action: PayloadAction<orderSummary>) {
      state.orderSummary = action.payload;
    },

    setOrderSummaryKey(
      state: OrdersState,
      action: PayloadAction<{ key: keyof orderSummary; value: any }>
    ) {
      const { key, value } = action.payload;
      if (state.orderSummary) {
        state.orderSummary[key] = value;
      }
    },

    // Set Margin
    setMargin(state, action: PayloadAction<Margin>) {
      state.margin = action.payload;
    },
    // SET MARGIN's Key
    setMarginKey(
      state: OrdersState,
      action: PayloadAction<{ key: keyof Margin; value: any }>
    ) {
      const { key, value } = action.payload;
      if (state.margin) {
        state.margin[key] = value;
      }
    },
  },
});

export const {
  resetChargesSummaryVisible,
  setChargesSummaryVisible,
  setChargesSummaryVisibleKey,
  setRemoteConfigGetter,
  setOrderSummaryKey,
  setOrderSummary,
  setMarginKey,
  setMargin,
} = OrdersSlice.actions;

export default OrdersSlice.reducer;
